import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactEChart from "echarts-for-react";
import { useSelector } from "react-redux";
import styles from "./shot-data-line-chart-widget.module.scss";
import type { WidgetRendererProps } from "../widget-renderer";
import { selectUnitsById } from "@src/reducers/units-slice";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { unitLabelOf } from "@src/cloud-connect/units/utils/unit-label-of";
import Spinner from "@src/components/atoms/spinner/spinner";
import { NoDataInTimeframe } from "@src/components/atoms/no-data-in-timeframe/no-data-in-timeframe";
import { useThemeColorsForCharts } from "../use-theme-colors-for-charts";
import { useRefetchWhenVisible } from "@src/hooks/use-check-widget-in-viewport";
import { getEventsForSourceUseCase } from "@src/cloud-connect/use-cases/get-events-for-source";
import { DEFAULT_POLLING_INTERVAL, resolvePollingMs } from "../constants";
import { eventsWindowParams, getWidgetWindow } from "../widget-query";
import {
  ExtractedShotData,
  ShotDataField,
  extractShotDataSeries,
} from "./extract-shot-data-series";
import { buildShotDataLineOption } from "./build-shot-data-line-option";
import { MULTI_LINE_DEFAULT_COLORS } from "../multi-line/build-multi-line-option";
import { scaleForField } from "../utils/value-scaling";
import { readAxisBounds } from "../axis-bounds";
import type { QueryBinding, WidgetEncoding } from "@src/types/dashboards";

const cssClassName = "shot-data-line-chart-widget";

/** The selected fields: bindings (pointers) joined with their role labels. */
const fieldsOf = (
  bindings: QueryBinding[],
  roles: WidgetEncoding["roles"],
  transforms: WidgetEncoding["transforms"],
): ShotDataField[] =>
  bindings.map((binding, index) => {
    const role = roles[`value_${index}`];
    const label =
      !Array.isArray(role) && role?.label
        ? role.label
        : binding.address.slice(binding.address.lastIndexOf("/") + 1);
    return {
      address: binding.address,
      label,
      color:
        (!Array.isArray(role) && role?.color) ||
        MULTI_LINE_DEFAULT_COLORS[index],
      scale: scaleForField(`value_${index}`, transforms),
    };
  });

/** Preview colours: two greens, so the picker tile cannot be mistaken for the
 *  multi-line tile (blue + green) or read as blue at all. Taken from the
 *  existing `preconfiguredColors` graph palette, so no new hex enters the app.
 *  Preview-only — a real chart keeps the palette the drawer assigned. */
const PREVIEW_COLORS = [
  "#00bfa0", // teal green
  "#50e991", // mint green
];

/** Mock for the picker tile and for a widget whose fields are not picked yet. */
const buildMockData = (count: number): ExtractedShotData => {
  const times = Array.from({ length: 8 }, (_v, i) =>
    new Date(Date.now() - (7 - i) * 10 * 60 * 1000).toISOString(),
  );
  return {
    times,
    series: Array.from({ length: count }, (_v, idx) => ({
      name: `Series ${idx + 1}`,
      values: times.map(
        (_t, i) =>
          Math.round((20 + idx * 15 + Math.sin(i + idx) * 6) * 100) / 100,
      ),
      color: PREVIEW_COLORS[idx % PREVIEW_COLORS.length],
    })),
  };
};

interface ShotDataFetchArgs {
  source?: string;
  eventType?: string;
  /** Stored range; turned into request parameters on every tick. */
  window: ReturnType<typeof getWidgetWindow>;
  /** Identity of `window`, so the effect re-runs when the range changes. */
  windowKey: string;
  bindingsKey: string;
  preview: boolean;
  inViewport: boolean;
  pollingMs: number | null;
}

const useShotDataFetch = ({
  source,
  eventType,
  window,
  windowKey,
  bindingsKey,
  preview,
  inViewport,
  pollingMs,
}: ShotDataFetchArgs) => {
  const [events, setEvents] = useState<unknown[]>([]);
  const [status, setStatus] = useState<"loading" | "ok" | "empty" | "error">(
    "loading",
  );
  // Last payload that arrived, to tell "this refresh failed" (keep the chart)
  // apart from "this widget has never loaded" (there is nothing to keep).
  const lastGoodRef = useRef<unknown[] | null>(null);

  // Visibility bridge — same role as the other chart widgets: an off-canvas
  // widget skips its fetches and refetches once when it scrolls back in.
  const latestLoadRef = useRef<(() => void) | null>(null);
  const inViewportRef = useRefetchWhenVisible(inViewport, latestLoadRef);

  useEffect(() => {
    if (preview || !source || !eventType) {
      latestLoadRef.current = null;
      setEvents([]);
      setStatus("empty");
      return;
    }
    let cancelled = false;
    let inFlight = false;
    let isInitialLoad = true;
    // A new machine / event type / range makes the drawn chart stale, so a
    // failure here IS a first failure and must be reported.
    lastGoodRef.current = null;

    const load = () => {
      // Overlap guard: a slow endpoint must not stack fetches when the
      // interval fires again mid-request.
      if (inFlight || document.hidden || !inViewportRef.current) return;
      inFlight = true;
      if (isInitialLoad) {
        isInitialLoad = false;
        setStatus("loading");
      }
      // Computed per tick, so a relative (duration) window slides forward
      // instead of staying pinned to the moment the widget mounted.
      getEventsForSourceUseCase({
        source,
        eventType,
        ...eventsWindowParams(window, Date.now()),
      })
        .then((result) => {
          if (cancelled) return;
          if (!result.success) {
            console.error("Failed to load events:", result.error);
            // A failed refresh must not throw away a chart that is already
            // drawn — and must not claim "no data" when the truth is "could
            // not fetch". Both messages share the same fallback, which is why
            // the drawn chart has to survive.
            if (lastGoodRef.current === null) {
              setStatus("error");
            }
            return;
          }
          const payload = Array.isArray(result.data) ? result.data : [];
          lastGoodRef.current = payload;
          setEvents(payload);
          // A healthy response with no events is "empty", not "error".
          setStatus(payload.length === 0 ? "empty" : "ok");
        })
        .catch(() => {
          // Never strand the guard, or polling goes dead for good.
          if (!cancelled && lastGoodRef.current === null) {
            setStatus("error");
          }
        })
        .finally(() => {
          inFlight = false;
        });
    };

    latestLoadRef.current = load;
    load();
    const intervalId =
      pollingMs != null ? setInterval(load, pollingMs) : undefined;
    return () => {
      cancelled = true;
      if (intervalId != null) clearInterval(intervalId);
      latestLoadRef.current = null;
    };
    // `window` and `inViewport` are read through the closure (via `window`
    //  and the `inViewportRef.current` guard inside `load()`); the signal
    //  that *should* re-run this effect is `windowKey` (range changes) plus
    //  the other real inputs. Including `inViewport` here would tear the
    //  effect down on every false→true flip — wiping `lastGoodRef` and
    //  flipping `status` back to "loading" so the spinner flashes and the
    //  "keep the drawn chart on failed background refresh" protection is
    //  destroyed by scrolling. The `useRefetchWhenVisible` bridge is the
    //  one and only driver of the re-entry refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, eventType, windowKey, bindingsKey, preview, pollingMs]);

  return { events, status };
};

const getUnitLabel = (
  presentation: WidgetRendererProps["presentation"],
  units: ReturnType<typeof selectUnitsById>,
) => unitLabelOf(getUnitById(units, presentation?.unit));

/** Re-measures the canvas when its tile changes size (column expand/collapse). */
const ResizeAwareChart = ({
  option,
  seriesKey,
  unitLabel,
}: {
  option: any;
  /** Identity of the drawn series (the picked values). A change means the
   *  chart must be recreated — see the `key` below. */
  seriesKey: string;
  /** Persistent unit label rendered top-right (unit id resolved via the slice). */
  unitLabel?: string;
}) => {
  const echartsRef = useRef<ReactEChart>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const resize = () =>
      echartsRef.current
        ?.getEchartsInstance()
        ?.resize({ width: "auto", height: "auto" });
    let lastW = -1;
    let lastH = -1;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect ?? {
        width: 0,
        height: 0,
      };
      if (width === lastW && height === lastH) return;
      lastW = width;
      lastH = height;
      requestAnimationFrame(resize);
    });
    observer.observe(el);
    // The tile's own layer: gridstack writes inline widths on it, so the
    // wrapper alone does not report the resize.
    const tileEl = el.closest(".grid-stack-item");
    if (tileEl && tileEl !== el) observer.observe(tileEl);
    const raf = requestAnimationFrame(resize);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={wrapperRef} className={styles[`${cssClassName}__chart`]}>
      {unitLabel ? (
        <span className={styles[`${cssClassName}__unit`]}>{unitLabel}</span>
      ) : null}
      <ReactEChart
        key={seriesKey}
        ref={echartsRef}
        style={{ width: "100%", height: "100%" }}
        option={option}
      />
    </div>
  );
};

export const ShotDataLineChartWidget = (props: WidgetRendererProps) => {
  const {
    title,
    query,
    preview = false,
    showTitle = true,
    inViewport = true,
  } = props;

  // Memoised on the two inputs that decide the result (the fallbacks live
  // inside, or a fresh array/object every render would defeat the memo).
  const fields = useMemo(
    () =>
      fieldsOf(
        query?.bindings ?? [],
        props.encoding?.roles ?? {},
        props.encoding?.transforms,
      ),
    [query?.bindings, props.encoding?.roles, props.encoding?.transforms],
  );
  const win = useMemo(() => getWidgetWindow(query), [query]);
  const windowKey = JSON.stringify(win ?? null);
  // The x-axis is the event number exactly when the range is a count.
  const useEventIndex = win?.size != null;

  // Identity of the drawn series: the picked values, in order. Used as the
  // chart's React key so a changed set recreates the chart (ECharts' default
  // merge would keep a removed series alive). Read from the bindings, not from
  // the option: renaming a series or a new value must not recreate the chart.
  const seriesKey = useMemo(
    () =>
      (query?.bindings ?? [])
        .map((binding) => `${binding.source ?? ""}\u0000${binding.address}`)
        .join("|"),
    [query?.bindings],
  );

  // Show mock data when nothing is picked yet (a freshly added widget) or in
  // the picker preview, exactly like the other chart widgets.
  const showMock = preview || fields.length === 0;

  const { events, status } = useShotDataFetch({
    source: query?.source,
    eventType: query?.event_type,
    window: win,
    windowKey,
    bindingsKey: seriesKey,
    preview: showMock,
    inViewport,
    pollingMs: resolvePollingMs(
      query?.sampling?.frequency ?? "raw",
      query?.sampling?.polling_interval ?? DEFAULT_POLLING_INTERVAL,
    ),
  });

  const extracted = useMemo(
    () =>
      showMock
        ? // Two lines for the unbound preview, so the tile reads as a chart.
          buildMockData(fields.length > 0 ? fields.length : 2)
        : extractShotDataSeries(events, fields),
    [showMock, fields, events],
  );

  // `preview` = picker tile: fixed dark chrome, so no dashboard palette.
  const themeColors = useThemeColorsForCharts(preview);
  const units = useSelector(selectUnitsById);
  const unitLabel = getUnitLabel(props.presentation, units);
  const bounds = readAxisBounds(props.semantics);
  const option = useMemo(
    () =>
      buildShotDataLineOption({
        series: extracted.series,
        times: extracted.times,
        useEventIndex,
        unitLabel,
        yMin: bounds?.min ?? null,
        yMax: bounds?.max ?? null,
        static: showMock,
        themeColors,
      }),
    [
      extracted,
      useEventIndex,
      unitLabel,
      bounds?.min,
      bounds?.max,
      showMock,
      themeColors,
    ],
  );

  // The chart tile is shared with the other line charts; only the x-axis
  // meaning changes with the range.
  const body = showMock ? (
    <ResizeAwareChart
      option={option}
      seriesKey={seriesKey}
      unitLabel={unitLabel}
    />
  ) : status === "loading" ? (
    <div className={styles[`${cssClassName}__chart`]}>
      <Spinner />
    </div>
  ) : status !== "ok" ? (
    <NoDataInTimeframe />
  ) : (
    <ResizeAwareChart
      option={option}
      seriesKey={seriesKey}
      unitLabel={unitLabel}
    />
  );

  return (
    <div className={styles[cssClassName]}>
      {showTitle && (
        <span className={styles[`${cssClassName}__title`]}>
          {title || "Shot Data Line Chart"}
        </span>
      )}
      {body}
    </div>
  );
};
